import { EnvironmentOutlined } from '@ant-design/icons';
import { FishingReport, Product } from '@omniafishing/core';
import classNames from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import React, { useEffect, useRef, useState } from 'react';
import iconPlay from '../../../../assets/icon-play.svg';
import { useReferenceData } from '../../../../hooks/use_reference_data';
import { getImgixPath } from '../../../../lib/imgix';
import { setShopifyImgWidth } from '../../../../lib/set_shopify_img_width';
import { toDollars } from '../../../../lib/to_dollars';
import { NAME_FALLBACK, roleAsText } from '../../../../lib/user';
import { AmbassadorColorDot } from '../../../ambassador_color_dot/ambassador_color_dot';
import { AmbassadorLink } from '../../../ambassador_link/ambassador_link';
import { ActiveMedia, MediaModal } from '../../../fishing_report_card/media_modal';
import ImageLoader from '../../../image_loader/image_loader';
import useImageLoader from '../../../image_loader/use_image_loader';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import { ProductLink } from '../../../product_link/product_link';
import { SeasonIcon } from '../../../season_icon/season_icon';
import { SpanLink } from '../../../span_link/span_link';
import SvgFire from '../../../svg/fire';
import { UserProfileImg } from '../../../user_profile_img/user_profile_img';

interface ProductWithHotbait extends Product {
  hotbait: boolean;
}

const ProductCard = ({ product }: { product: ProductWithHotbait }) => {
  const { allBrands } = useReferenceData();
  const {
    img_url,
    title_short,
    price,
    hotbait,
    compare_at_price,
    on_sale,
    in_stock,
    inventory_tracked,
  } = product;
  const brand = allBrands.find((b) => b.name === product.brand);
  const availableForSale = in_stock || !inventory_tracked;

  return (
    <ProductLink
      currentColor
      product={product}
      className=" bg-white rounded-md border p-3 h-full flex flex-col gap-1 select-none relative"
    >
      <img src={setShopifyImgWidth(img_url, 200)} alt="" />
      {brand && <p className="uppercase text-xs text-[#657d7d]">{brand.display_name}</p>}
      <p className="font-medium leading-tight">{title_short}</p>

      <div className="mt-aut  o flex justify-between gap-1 items-end">
        <p className="mt-auto font-medium text-base flex flex-col-reverse leading-tight">
          <span
            className={classNames({
              'text-[#d8363e]': on_sale,
            })}
          >
            ${price}
          </span>
          {on_sale && (
            <s className={'opacity-50 whitespace-nowrap'}>{toDollars(compare_at_price)}</s>
          )}
        </p>
        <OmniaButton kind="primary" size="sm" isDisabled={!availableForSale}>
          {availableForSale ? 'Add to Cart' : 'Out of Stock'}
        </OmniaButton>
      </div>

      {hotbait && <SvgFire className="absolute top-3 left-3 text-[#fb2422] text-2xl" />}
    </ProductLink>
  );
};

interface MapFishingReportCardProps {
  report: FishingReport;
  showLakeName?: boolean;
  wideView?: boolean;
}
export const MapFishingReportCard = (props: MapFishingReportCardProps) => {
  const { report, showLakeName = true, wideView } = props;
  const {
    image,
    video,
    waterbody,
    species,
    technique,
    featured_product,
    season,
    structure,
    comments,
    user,
    products,
    id,
  } = report;

  const hasImage = !!image;
  const hasVideo = !!video;

  const hasImageAndVideo = hasImage && hasVideo;
  const mediaType = hasImageAndVideo ? 'image' : hasImage ? 'image' : 'video';

  const [activeExpandedMedia, setActiveExpandedMedia] = useState<ActiveMedia>(null);
  const { isImageLoading, isImageError, handleImageLoad, handleImageError } = useImageLoader();

  const imagePath =
    hasImage &&
    getImgixPath(image, {
      w: 600,
    });

  const imageVideoPath =
    hasVideo &&
    getImgixPath(video.image, {
      w: 600,
    });

  const Video = video && (
    <>
      <ImageLoader
        isImageError={isImageError}
        isImageLoading={isImageLoading}
        loadingClassName={'absolute top-0 left-0 w-full h-full flex justify-center items-center'}
        onError={handleImageError}
        onLoad={(e) => {
          handleImageLoad();
        }}
        onClick={() => {
          setActiveExpandedMedia({ type: 'video', src: video.src });
        }}
        src={imageVideoPath}
      />
      {!isImageError && (
        <img
          src={iconPlay}
          className={
            'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none'
          }
        />
      )}
    </>
  );

  const Image = image && (
    <>
      <ImageLoader
        className={'w-full h-full object-cover max-h-[320px]'}
        isImageError={isImageError}
        isImageLoading={isImageLoading}
        loadingClassName={'absolute top-0 left-0 w-full h-full flex justify-center items-center'}
        onError={handleImageError}
        onLoad={(e) => {
          handleImageLoad();
        }}
        onClick={() => {
          setActiveExpandedMedia({ type: 'img', src: imagePath });
        }}
        src={imagePath}
      />
    </>
  );

  const userFullName = user.full_name || NAME_FALLBACK;

  const textRef = useRef<HTMLQuoteElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      const isTruncated = element.scrollHeight > element.clientHeight;
      setIsTextTruncated(isTruncated);
    }
  }, []);

  const [emblaRef] = useEmblaCarousel(
    {
      align: 'start',
      dragFree: true,
    },
    []
  );

  const productsToRender = [featured_product, ...products].filter(Boolean);

  return (
    <>
      <div className="rounded-lg bg-white p-3 flex flex-col gap-3" data-id={id}>
        <div className="flex items-center gap-2">
          <div>
            <AmbassadorLink ambassador={user}>
              <UserProfileImg image={user.image} imageSize={100} size={50} />
            </AmbassadorLink>
          </div>
          <div className="flex flex-col ">
            <AmbassadorLink ambassador={user}>
              <strong>{userFullName}</strong>
              <span className="flex items-center gap-1">
                <AmbassadorColorDot user={user} bordered />
                <span>{roleAsText(user.role)} Ambassador</span>
              </span>
            </AmbassadorLink>
          </div>
        </div>
        <div ref={emblaRef} className="overflow-hidden -mx-6">
          <div className="flex select-none gap-3 px-6">
            {(hasImage || hasVideo) && (
              <div
                className={classNames('w-3/5 flex-grow-0 flex-shrink-0', {
                  'sm:w-2/5': wideView,
                })}
              >
                <div className=" bg-white rounded-md border h-full flex overflow-hidden cursor-pointer relative">
                  {mediaType === 'image' ? Image : Video}
                </div>
              </div>
            )}
            {productsToRender.map((p) => {
              const productWithHotbait = {
                ...p,
                hotbait: p.sku === featured_product?.sku,
              };
              return (
                <div
                  className={classNames('w-3/5 flex-grow-0 flex-shrink-0', {
                    'sm:w-2/5': wideView,
                  })}
                  key={p.sku}
                >
                  <ProductCard product={productWithHotbait} />
                </div>
              );
            })}
          </div>
        </div>
        <div>
          {showLakeName && (
            <div className="flex justify-between border-b border-black py-1">
              <span></span>
              <span className="font-bold">
                <EnvironmentOutlined /> {waterbody.primary_name}
              </span>
            </div>
          )}
          <div className="flex justify-between border-b border-black py-1">
            <span>Species</span>
            <span className="flex items-center gap-1">
              {species.image && <img src={species.image} width="28" />}
              {species.display_name}
            </span>
          </div>
          <div className="flex justify-between border-b border-black py-1">
            <span>Technique</span>
            <span>{technique.display_name}</span>
          </div>
          <div className="flex justify-between border-b border-black py-1">
            <span>Season</span>
            <span className="flex items-center gap-1">
              <SeasonIcon seasonName={season.name} tag="span" />
              {season.display_name}
            </span>
          </div>
          <div className="flex justify-between border-b border-black py-1">
            <span>Structure</span>
            <span>{structure.display_name}</span>
          </div>
        </div>
        {comments && (
          <div className="flex flex-col gap-0.5 text-xs text-neutral-500">
            <q
              ref={textRef}
              className={classNames('text-ellipsis line-clamp-2', {
                'line-clamp-none': isExpanded,
              })}
            >
              {comments}
            </q>
            {isTextTruncated && !isExpanded && (
              <SpanLink
                className="ml-auto"
                onClick={() => {
                  setIsExpanded(true);
                }}
              >
                See More
              </SpanLink>
            )}
          </div>
        )}
      </div>
      <MediaModal
        activeMedia={activeExpandedMedia}
        onCancel={() => {
          setActiveExpandedMedia(null);
        }}
      />
    </>
  );
};
